import { api } from './client'

/**
 * L'effort, les faiblesses et le classement — 02 §F2.2, §F2.5, §F5.3.
 *
 * Trois lectures de rythmes différents, donc trois appels : l'objectif du jour
 * se rafraîchit à chaque page du Fil, les faiblesses une fois par écran, le
 * classement une fois par heure. Les fondre en un seul obligerait à recalculer
 * les deux derniers dix fois par minute pour rien.
 */

export interface Effort {
  total_xp: number
  /** Jours travaillés d'affilée. Les jours pardonnés n'y comptent PAS. */
  serie: number
  record: number
  total_reponses: number
  jours_actifs: number
  /** Le jour pardonné de la série en cours (RG-2.18), `AAAA-MM-JJ`. */
  jour_de_grace: string | null
  serie_en_danger: boolean
}

export interface ObjectifDuJour {
  cible_xp: number
  xp_du_jour: number
  atteint: boolean
  reponses_du_jour: number
  minutes_du_jour: number
}

export interface JourDActivite {
  date: string
  xp: number
  reponses: number
  atteint: boolean
}

export interface Progression {
  effort: Effort
  objectif: ObjectifDuJour
  activite: JourDActivite[]
}

export interface Faiblesse {
  tag_id: number
  nom: string
  chemin: string
  /** Le taux RÉCENT — c'est lui qui trie, pas la maîtrise (RG-P33). */
  taux_recent: number
  maitrise_pct: number
  niveau: number
  /** Trop peu de preuves pour être affirmatif : l'interface DOIT le dire. */
  indicatif: boolean
}

/** RG-5.13 — cinq métriques, toutes d'effort. Aucune de performance. */
export type MetriqueDuClassement =
  | 'responses'
  | 'badges'
  | 'current_streak'
  | 'longest_streak'
  | 'xp'

/** RG-5.15 — la semaine est l'onglet par défaut : elle se remet à zéro. */
export type PeriodeDuClassement = 'week' | 'month' | 'all_time'

export interface LigneDeClassement {
  rang: number
  valeur: number
  pseudonyme: string
  avatar: string | null
  moi: boolean
}

export interface Classement {
  data: LigneDeClassement[]
  meta: {
    periode: PeriodeDuClassement
    metrique: MetriqueDuClassement
    titre: string
    precision: string
    unite: string
    total_classes: number
    /** RG-5.19 — servi même hors des cent premiers : « 342ᵉ sur 1 891 ». */
    mon_rang: number | null
    ma_valeur: number | null
    je_participe: boolean
    /** Une série ne se découpe pas en semaines : la période a été ignorée. */
    periode_ignoree: boolean
  }
}

export function progression(): Promise<{ data: Progression }> {
  return api<{ data: Progression }>('/progression')
}

/**
 * Un thème qui pèse lourd au concours et que le candidat NE SUIT PAS — RG-P34.
 *
 * Ce n'est PAS une faiblesse : une faiblesse est mesurée, on sait que le
 * candidat s'y trompe. Un angle mort n'est mesuré nulle part, précisément
 * parce qu'il n'a jamais été rencontré. D'où un type et un bloc distincts.
 */
export interface AngleMort {
  tag_id: number
  nom: string
  chemin: string
  /** La part mesurée du concours, récence comprise (D-13, D-32). */
  part_pct: number
  sujets_analyses: number
  concours: string
}

export function faiblesses(limite = 10): Promise<{ data: Faiblesse[] }> {
  return api<{ data: Faiblesse[] }>(`/faiblesses?limite=${String(limite)}`)
}

/**
 * Deux appels et non un : les faiblesses bougent à chaque session, les angles
 * morts seulement quand les poids sont recalculés ou qu'un tag est suivi. Les
 * servir ensemble ferait relire les seconds dix fois par heure pour rien.
 */
export function anglesMorts(): Promise<{ data: AngleMort[] }> {
  return api<{ data: AngleMort[] }>('/angles-morts')
}

export function classement(
  metrique: MetriqueDuClassement,
  periode: PeriodeDuClassement,
): Promise<Classement> {
  return api<Classement>(`/classement?metrique=${metrique}&periode=${periode}`)
}
